import React, {
  useEffect, useRef, useState,
} from 'react';
import { KatModal, KatProgress, KatIcon } from '@amzn/katal-react';
import { SuButton } from '@amzn/seller-university-ui-components';
import {
  useStartAssessmentMutation,
  useSaveAnswerMutation,
  useSubmitAssessmentMutation,
} from 'src/redux/slices/api/su';
import { SubmitAssessmentPOSTResponse } from 'src/types/api/assessment';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface AssessmentOverlayCopy {
  title: string;
  questionProgressLabel: (current: number, total: number) => string;
  backLabel: string;
  continueLabel: string;
  submitLabel: string;
  closeLabel: string;
  retakeLabel: string;
  downloadCertificationLabel: string;
  savingProgressLabel: string;
  progressSavedLabel: string;
  saveFailedLabel: string;
  passedTitle: string;
  failedTitle: string;
  correctCountLabel: (correct: number, total: number) => string;
  passedBody: string;
  failedBody: string;
  resolveQuestionText: (promptId: string) => string;
  resolveOptionText: (textId: string) => string;
}

export interface AssessmentOverlayProps {
  credentialId: string;
  clientId: string;
  selLocale?: string;
  copy: AssessmentOverlayCopy;
  completionCertificateUrl?: string;
  onClose: () => void;
  onPassed?: (result: SubmitAssessmentPOSTResponse) => void;
  onSubmitted?: () => void;
  onAssessmentStarted?: (attemptId: string) => void;
}

export const AssessmentOverlay: React.FC<AssessmentOverlayProps> = ({
  credentialId,
  clientId,
  selLocale,
  completionCertificateUrl,
  copy,
  onClose,
  onPassed,
  onSubmitted,
  onAssessmentStarted,
}) => {
  const [startAssessment, { data: attempt, isLoading: isStarting, isError: isStartError }] = useStartAssessmentMutation();
  const [
    saveAnswer,
    {
      isLoading: isSavingAnswer, isSuccess: hasSavedAnswer, isError: hasSaveError, reset: resetSaveState,
    },
  ] = useSaveAnswerMutation();
  const [submitAssessment, { isLoading: isSubmitting }] = useSubmitAssessmentMutation();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptionByQuestion, setSelectedOptionByQuestion] = useState<Record<string, string>>({});
  const [result, setResult] = useState<SubmitAssessmentPOSTResponse | null>(null);
  // Question count captured when the attempt is submitted (attempt is guaranteed present there), so
  // the result view can derive the "N out of M correct" count without re-reading the optional attempt.
  const [scoredQuestionCount, setScoredQuestionCount] = useState(0);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
  // Per-mount latch so we notify the parent at most once per mount; the parent dedupes by
  // attemptId across modal reopens/resume.
  const hasNotifiedStartedRef = useRef(false);
  const { clickMetric, viewMetric } = useMetrics();

  useEffect(() => {
    startAssessment({ credentialId, clientId, selLocale });
  }, [startAssessment, credentialId, clientId, selLocale]);

  useEffect(() => {
    if (isStartError) {
      viewMetric({
        feature: FeatureName.AssessmentError,
        metadata: { credentialId, phase: 'start' },
      });
    }
  }, [isStartError, viewMetric, credentialId]);

  useEffect(() => {
    if (attempt) {
      setSelectedOptionByQuestion(attempt.answers);
      // Resume on the first unanswered question; if all are answered, land on the last one.
      const firstUnansweredIndex = attempt.questions.findIndex(
        (question) => !(question.questionId in attempt.answers),
      );
      setCurrentIndex(
        firstUnansweredIndex === -1
          ? Math.max(0, attempt.questions.length - 1)
          : firstUnansweredIndex,
      );
    }
  }, [attempt]);

  const handleRetake = () => {
    setResult(null);
    setCurrentIndex(0);
    setSelectedOptionByQuestion({});
    setHasSubmitError(false);
    // Reset the latch so the retake's new attemptId is reported to the parent (which re-emits).
    hasNotifiedStartedRef.current = false;
    startAssessment({ credentialId, clientId, selLocale });
  };

  const handleClose = () => {
    if (!result) {
      clickMetric({
        feature: FeatureName.AssessmentExited,
        metadata: {
          credentialId,
          attemptId: attempt?.attemptId,
          questionIndex: currentIndex,
          totalQuestions: attempt?.questions.length,
          answeredCount: Object.keys(selectedOptionByQuestion).length,
        },
      });
    }
    onClose();
  };

  if (result) {
    const isPassed = result.outcome === 'PASSED';
    // Question count captured at submit time (attempt was guaranteed present then).
    const totalQuestions = scoredQuestionCount;
    // DISPLAY-DERIVED correct count, NOT authoritative: the Submit response carries only scorePercent
    // (SubmitAssessmentPOSTResponse = { outcome, scorePercent }), so we reconstruct the count from
    // the percentage. This is provably exact for the pilot's 15-question assessment (each 6.667%
    // step maps to a distinct integer percent), but at other question counts rounding can misreport
    // by one. The durable fix is a real correctCount returned by NES via SubmitAssessmentOutput.
    const correctCount = Math.round((result.scorePercent / 100) * totalQuestions);
    return (
      <KatModal
        className="kat-modal-assessment-result"
        visible
        onClose={handleClose}
        title={copy.title}
        footer={(
          // Per the mocks the footer holds only Close, as a tertiary button anchored to the right
          // corner. The primary action (Download on pass, Re-take on fail) lives in the modal body
          // under the message (see below).
          <div className="kat-group-horizontal flex justify-end gap-su-space-03">
            <SuButton variant="tertiary" label={copy.closeLabel} onClick={handleClose} />
          </div>
        )}
      >
        {/* px-[4.5rem] (72px) is a literal because Katal's spacing scale stops at
            --kat-space-10 (48px) and the mock measures ~74px of horizontal padding. That
            padding sets the content column to ~620px, which is what produces the mock's
            line breaks. Note the exact break points only hold for English. */}
        <div className="flex items-center gap-su-space-08 px-[4.5rem] py-su-space-07" aria-live="polite" role="status">
          <KatProgress
            type="circular"
            size="large"
            value={result.scorePercent}
            ref={(node) => {
              if (!node) return;
              // Arc + centre percentage: the saturated status text colour (matches the arc to the label).
              const color = isPassed ? 'var(--kat-text-status-success)' : 'var(--kat-text-status-danger)';
              // Interior disc: the PALE tinted-surface family (--kat-background-message-*), NOT the
              // solid --kat-background-status-* fills, which render as a saturated filled disc.
              const tint = isPassed ? 'var(--kat-background-message-success)' : 'var(--kat-background-message-danger)';
              const host = node as unknown as HTMLElement;
              host.style.setProperty('--kat-progress-track-foreground', color);
              // Unfilled remainder of the track: TRANSPARENT so the host's tinted background shows
              // through it. Setting it white instead leaves a visible pale band between the tinted
              // interior and the arc, which the mockup does not have -- the mock shows the tint
              // filling to the edge with only the solid arc drawn over it.
              host.style.setProperty('--kat-progress-track-background', 'transparent');
              // --kat-progress-circular-size is a MULTIPLIER BASE, not a final pixel size: Katal's
              // shadow CSS renders the SVG wrapper at base x {small:1, medium:2, large:4}. With
              // size="large" the final diameter is base x 4, so 17px -> 68px to match the mockup.
              host.style.setProperty('--kat-progress-circular-size', '17px');
              host.style.setProperty('--kat-progress-value-color', color);
              host.style.setProperty('--kat-progress-value-font-weight', '700');
              host.style.setProperty('--value-color', color);
              host.style.setProperty('--value-font-weight', '700');
              host.style.setProperty('--value-font-size', '1.25rem');
              host.style.background = tint;
              host.style.borderRadius = '50%';
              // The ring is decorative: the adjacent text score already conveys the result, and the
              // ring's own role=progressbar / aria-live would otherwise double-announce inside this
              // role=status region. Hide it from assistive tech, mirroring the decorative-span
              // precedent used for the option radio markers below.
              host.setAttribute('aria-hidden', 'true');
            }}
          />
          {/* m-0 on the children: this package disables Tailwind preflight, so h2/p keep their
              user-agent margins, and margins do NOT collapse inside a flex column -- they would
              stack on top of `gap` and roughly double every space. */}
          <div className="flex flex-col gap-su-space-06">
            <h2 className="m-0 text-su-heading-500">{isPassed ? copy.passedTitle : copy.failedTitle}</h2>
            <p className={`m-0 text-su-body-300 font-bold ${isPassed ? 'text-su-text-status-success' : 'text-su-text-status-danger'}`}>
              {copy.correctCountLabel(correctCount, totalQuestions)}
            </p>
            <p className="m-0 text-su-body-300">{isPassed ? copy.passedBody : copy.failedBody}</p>
            <div>
              {isPassed ? (
                completionCertificateUrl && (
                  <SuButton
                    variant="primary"
                    label={copy.downloadCertificationLabel}
                    icon={<KatIcon name="get_app" size="small" />}
                    onClick={() => {
                      // Metric only; linkProps performs the download. Surface tag separates it from the completion-panel one.
                      clickMetric({
                        feature: FeatureName.CredentialCertificateDownloaded,
                        metadata: { credentialId, surface: 'assessment_result' },
                      });
                    }}
                    linkProps={{
                      href: completionCertificateUrl,
                      refTag: 'su_assessment_certificate_download',
                    }}
                  />
                )
              ) : (
                <SuButton
                  variant="primary"
                  label={copy.retakeLabel}
                  icon={<KatIcon name="refresh" size="small" />}
                  onClick={handleRetake}
                />
              )}
            </div>
          </div>
        </div>
      </KatModal>
    );
  }

  if (isStarting) {
    return (
      <KatModal visible onClose={handleClose} title={copy.title}>
        <LoadingView />
      </KatModal>
    );
  }

  if (isStartError || !attempt || attempt.questions.length === 0) {
    return (
      <KatModal visible onClose={handleClose} title={copy.title}>
        <ErrorView />
      </KatModal>
    );
  }

  const { attemptId, questions } = attempt;
  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex];
  const selectedOptionId = selectedOptionByQuestion[currentQuestion.questionId];
  const isLastQuestion = currentIndex === totalQuestions - 1;
  const questionProgressValue = Math.round((currentIndex / totalQuestions) * 100);

  const handleSelectOption = (questionId: string, optionId: string) => {
    setSelectedOptionByQuestion((prev) => ({ ...prev, [questionId]: optionId }));
    // Fire-and-forget autosave (not unwrapped): a failed save is non-critical and re-saves on the
    // next change. The header indicator reports its progress from the mutation state.
    saveAnswer({
      credentialId, attemptId, questionId, selectedOptionId: optionId,
    });
  };

  const handleBack = () => {
    // The indicator reports the answer just saved, so clear it when leaving the question.
    resetSaveState();
    setCurrentIndex((index) => Math.max(0, index - 1));
  };

  const handleContinue = async () => {
    // First Next of this mount with a response selected: notify the parent, which dedupes
    // AssessmentStarted by attemptId across modal reopens/resume.
    if (!hasNotifiedStartedRef.current && selectedOptionId) {
      hasNotifiedStartedRef.current = true;
      onAssessmentStarted?.(attemptId);
    }
    if (!isLastQuestion) {
      resetSaveState();
      setCurrentIndex((index) => Math.min(totalQuestions - 1, index + 1));
      return;
    }
    setHasSubmitError(false);
    try {
      const submitted = await submitAssessment({ credentialId, attemptId }).unwrap();
      clickMetric({
        feature: FeatureName.AssessmentSubmitted,
        metadata: { credentialId, attemptNumber: submitted.attemptNumber, scorePercent: submitted.scorePercent },
      });
      setResult(submitted);
      // Fires for a pass and a fail alike: the attempt is now scored, so the page must refresh
      // the credential it caches or the assessment row keeps offering Resume.
      onSubmitted?.();
      // Record the count now (attempt is in scope here) so the result view can derive the
      // "N out of M correct" line from the score percentage.
      setScoredQuestionCount(totalQuestions);
      if (submitted.outcome === 'PASSED') {
        viewMetric({
          feature: FeatureName.AssessmentPassed,
          metadata: { credentialId, scorePercent: submitted.scorePercent, attemptNumber: submitted.attemptNumber },
        });
        onPassed?.(submitted);
      } else if (submitted.outcome === 'NOT_PASSED') {
        viewMetric({
          feature: FeatureName.AssessmentNotPassed,
          metadata: { credentialId, scorePercent: submitted.scorePercent, attemptNumber: submitted.attemptNumber },
        });
      }
    } catch {
      setHasSubmitError(true);
      viewMetric({
        feature: FeatureName.AssessmentError,
        metadata: { credentialId, phase: 'submit' },
      });
    }
  };

  // Autosave indicator, derived straight from the mutation state: it shows while a save is in
  // flight and the confirmation then stays up until the next save replaces it.
  let saveStatus: SaveStatus = 'idle';
  if (hasSaveError) {
    saveStatus = 'error';
  } else if (isSavingAnswer) {
    saveStatus = 'saving';
  } else if (hasSavedAnswer) {
    saveStatus = 'saved';
  }
  const saveStatusLabel = {
    idle: '',
    saving: copy.savingProgressLabel,
    saved: copy.progressSavedLabel,
    error: copy.saveFailedLabel,
  }[saveStatus];

  return (
    <KatModal
      visible
      className="kat-modal-assessment-question"
      onClose={handleClose}
      footer={(
        <div className="kat-group-horizontal flex justify-end gap-su-space-03">
          {/* Back is omitted entirely on the first question (not merely disabled) and appears from
              question 2 onward. It is a tertiary action alongside the primary Continue/Submit. */}
          {currentIndex > 0 && (
            <SuButton
              variant="tertiary"
              label={copy.backLabel}
              onClick={handleBack}
            />
          )}
          <SuButton
            variant="primary"
            label={isLastQuestion ? copy.submitLabel : copy.continueLabel}
            loading={isSubmitting}
            disabled={!selectedOptionId}
            onClick={handleContinue}
          />
        </div>
      )}
    >
      <span slot="title" className="flex w-full items-center justify-between gap-su-space-05">
        <span className="text-wrap">{copy.title}</span>
        <span
          role="status"
          aria-live="polite"
          className={`shrink-0 font-normal text-su-body-300 ${saveStatus === 'error' ? 'text-su-text-status-danger' : 'text-su-text-link'}`}
        >
          {saveStatusLabel}
        </span>
      </span>
      <div className="flex flex-col gap-su-space-04 px-su-space-05 py-su-space-04">
        <div className="flex flex-col gap-su-space-02">
          <div className="flex items-center justify-between">
            <span className="text-su-text-secondary">
              {copy.questionProgressLabel(currentIndex + 1, totalQuestions)}
            </span>
            <span className="text-su-text-secondary">{`${questionProgressValue}%`}</span>
          </div>
          <KatProgress type="linear" value={questionProgressValue} hideValue />
        </div>
        <p id={`${currentQuestion.questionId}-prompt`}>{copy.resolveQuestionText(currentQuestion.prompt)}</p>
        <div
          role="radiogroup"
          aria-labelledby={`${currentQuestion.questionId}-prompt`}
          className="flex flex-col gap-su-space-03"
        >
          {currentQuestion.options.map((option, optionIndex) => {
            const isSelected = selectedOptionId === option.optionId;
            const selectOption = () => handleSelectOption(currentQuestion.questionId, option.optionId);
            const selectedIndex = currentQuestion.options.findIndex(
              (o) => o.optionId === selectedOptionId,
            );
            const isTabStop = selectedIndex === -1 ? optionIndex === 0 : isSelected;
            const focusOptionAt = (index: number) => {
              const { length } = currentQuestion.options;
              const nextIndex = (index + length) % length;
              const nextOption = currentQuestion.options[nextIndex];
              handleSelectOption(currentQuestion.questionId, nextOption.optionId);
              optionRefs.current[nextIndex]?.focus();
            };
            return (
              <div
                key={option.optionId}
                ref={(node) => { optionRefs.current[optionIndex] = node; }}
                role="radio"
                aria-checked={isSelected}
                tabIndex={isTabStop ? 0 : -1}
                className={`flex items-center gap-su-space-03 rounded border-su-border-width-xxsmall border-solid p-su-space-03 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-su-border-item-selected ${
                  isSelected
                    ? 'border-su-border-item-selected bg-su-background-item-selected hover:bg-su-background-item-selected-hover'
                    : 'border-su-border-item-default bg-su-background-container-content hover:bg-su-background-item-hover'
                }`}
                onClick={selectOption}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectOption();
                  } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                    e.preventDefault();
                    focusOptionAt(optionIndex + 1);
                  } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                    e.preventDefault();
                    focusOptionAt(optionIndex - 1);
                  }
                }}
              >
                <span
                  aria-hidden="true"
                  className={`size-su-space-04 shrink-0 rounded-full border-su-border-width-small border-solid ${
                    isSelected
                      ? 'border-su-border-item-selected bg-su-border-item-selected'
                      : 'border-su-border-item-default'
                  }`}
                />
                <span>{copy.resolveOptionText(option.text)}</span>
              </div>
            );
          })}
        </div>
        <div aria-live="assertive" role="alert">
          {hasSubmitError && <ErrorView />}
        </div>
      </div>
    </KatModal>
  );
};
